import { supabase } from '../lib/supabase'
import type { CallSheet } from '../types/portal'

/**
 * Call sheet PDFs.
 *
 * Stored in Supabase Storage, not the browser: a `URL.createObjectURL` link
 * only ever pointed at a blob that lived in the tab that made it, so nothing
 * else — another device, another staff member, the client on a shared link —
 * could ever actually open it. This is the single write and remove path, so
 * nothing is reported as saved until it genuinely is.
 */

const BUCKET = 'call-sheets'
const MAX_BYTES = 25 * 1024 * 1024

function sanitize(name: string): string {
  const cleaned = name.trim().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-')
  return cleaned.slice(-120) || 'call-sheet.pdf'
}

function id(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export interface UploadOutcome {
  ok: boolean
  callSheet?: CallSheet
  error?: string
}

/** Stores a call sheet PDF and returns its permanent, shareable link. */
export async function uploadCallSheet(projectId: string, file: File): Promise<UploadOutcome> {
  if (!supabase) return { ok: false, error: 'No database connection.' }
  if (!projectId) return { ok: false, error: 'Save the project before adding a call sheet.' }

  const looksLikePdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  if (!looksLikePdf) return { ok: false, error: 'Call sheets must be a PDF.' }
  if (file.size > MAX_BYTES) return { ok: false, error: 'That PDF is larger than 25MB.' }

  const path = `${projectId}/${id()}-${sanitize(file.name)}`

  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: 'application/pdf',
    upsert: false,
  })

  if (error) {
    console.warn('[supabase] call sheet upload failed:', error.message)
    return { ok: false, error: 'The call sheet could not be saved. Nothing was stored.' }
  }

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return { ok: true, callSheet: { fileName: file.name, path, url: data.publicUrl } }
}

/** Best-effort cleanup when a call sheet is replaced or removed. */
export async function removeCallSheet(path: string | undefined): Promise<void> {
  if (!supabase || !path) return
  const { error } = await supabase.storage.from(BUCKET).remove([path])
  if (error) console.warn('[supabase] call sheet removal failed:', error.message)
}
